import React, { useState, useEffect } from "react";
import { useAuthContext } from "app/contexts/auth/context";
import { createPortal } from "react-dom";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  DocumentIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  CalendarIcon,
  UserIcon,
  FolderIcon,
  InformationCircleIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  XCircleIcon,
  TableCellsIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { Button, Badge, Avatar } from "components/ui";
import clsx from "clsx";
import { formatFileSize, getVerificationMetadataLabels, getInitials, getUploaderName, getModifierName, downloadDocumentWithToast } from "../utils/documentUtils";
import { getLeadDocumentPreviewUrl } from "services/leadsService";
import { getJobDocumentPreviewUrl } from "services/jobsService";
import { getAttachmentPreviewUrl } from "services/documentUploadService";
import { DOCUMENT_ENTITY_TYPES } from "services/documentFolderService";
import { colorFromText } from "utils/colorFromText";
import { formatBytes } from "utils/formatBytes";
import { formatDisplayDate } from "utils/dateUtils";
import { toast } from "sonner";

// Enhanced file type configurations with modern colors and gradients
const FILE_TYPES = {
  PDF: {
    extensions: [".pdf"],
    icon: DocumentIcon,
    color: "text-red-600",
    bgColor: "bg-gradient-to-br from-red-50 to-red-100",
    borderColor: "border-red-200",
    darkBgColor: "dark:from-red-900/20 dark:to-red-800/30",
    label: "PDF Document",
  },
  DOCX: {
    extensions: [".docx", ".doc"],
    icon: DocumentIcon,
    color: "text-blue-600",
    bgColor: "bg-gradient-to-br from-blue-50 to-blue-100",
    borderColor: "border-blue-200",
    darkBgColor: "dark:from-blue-900/20 dark:to-blue-800/30",
    label: "Word Document",
  },
  IMAGE: {
    extensions: [".jpg", ".jpeg", ".png", ".gif", ".webp"],
    icon: PhotoIcon,
    color: "text-green-600",
    bgColor: "bg-gradient-to-br from-green-50 to-green-100",
    borderColor: "border-green-200",
    darkBgColor: "dark:from-green-900/20 dark:to-green-800/30",
    label: "Image File",
  },
  EXCEL: {
    extensions: [".xlsx", ".xls"],
    icon: DocumentIcon,
    color: "text-emerald-600",
    bgColor: "bg-gradient-to-br from-emerald-50 to-emerald-100",
    borderColor: "border-emerald-200",
    darkBgColor: "dark:from-emerald-900/20 dark:to-emerald-800/30",
    label: "Excel Spreadsheet",
  },
  POWERPOINT: {
    extensions: [".pptx", ".ppt"],
    icon: DocumentIcon,
    color: "text-orange-600",
    bgColor: "bg-gradient-to-br from-orange-50 to-orange-100",
    borderColor: "border-orange-200",
    darkBgColor: "dark:from-orange-900/20 dark:to-orange-800/30",
    label: "PowerPoint Presentation",
  },
  TEXT: {
    extensions: [".txt", ".csv", ".json", ".md", ".xml"],
    icon: DocumentIcon,
    color: "text-gray-600",
    bgColor: "bg-gradient-to-br from-gray-50 to-gray-100",
    borderColor: "border-gray-200",
    darkBgColor: "dark:from-gray-900/20 dark:to-gray-800/30",
    label: "Text File",
  },
  DEFAULT: {
    extensions: [],
    icon: DocumentIcon,
    color: "text-gray-600",
    bgColor: "bg-gradient-to-br from-gray-50 to-gray-100",
    borderColor: "border-gray-200",
    darkBgColor: "dark:from-gray-900/20 dark:to-gray-800/30",
    label: "Document",
  },
};

// Utility function to get file type category
const getFileTypeCategory = (fileName) => {
  if (!fileName || typeof fileName !== "string") {
    return { category: "UNKNOWN", config: FILE_TYPES.DEFAULT };
  }

  const parts = fileName.split(".");
  if (parts.length < 2) {
    return { category: "UNKNOWN", config: FILE_TYPES.DEFAULT };
  }

  const extension = "." + parts.pop().toLowerCase();

  for (const [category, config] of Object.entries(FILE_TYPES)) {
    if (config.extensions.includes(extension)) {
      return { category, config };
    }
  }
  return { category: "UNKNOWN", config: FILE_TYPES.DEFAULT };
};

// Utility function to format date
const formatDate = (dateString) => {
  return formatDisplayDate(dateString);
};

// Helper functions for history display
const getActionTypeColor = (actionType) => {
  switch (actionType) {
    case 1: // Verified
      return "success";
    case 2: // Rejected
      return "error";
    case 3: // Final Approved
      return "primary";
    default:
      return "neutral";
  }
};

const getActionTypeBgColor = (actionType) => {
  switch (actionType) {
    case 1: // Verified
      return "bg-green-100 dark:bg-green-900/30";
    case 2: // Rejected
      return "bg-red-100 dark:bg-red-900/30";
    case 3: // Final Approved
      return "bg-blue-100 dark:bg-blue-900/30";
    default:
      return "bg-gray-100 dark:bg-gray-900/30";
  }
};

const getActionTypeDotColor = (actionType) => {
  switch (actionType) {
    case 1: // Verified
      return "bg-green-500";
    case 2: // Rejected
      return "bg-red-500";
    case 3: // Final Approved
      return "bg-blue-500";
    default:
      return "bg-gray-500";
  }
};

const parseCsvText = (text) => {
  if (!text) return { headers: [], rows: [] };
  // Auto-detect delimiter from first line
  const firstLine = text.split(/\r?\n/)[0] || "";
  let delimiter = ",";
  if (!firstLine.includes(",") && firstLine.includes(";")) {
    delimiter = ";";
  } else if (!firstLine.includes(",") && firstLine.includes("\t")) {
    delimiter = "\t";
  }

  const lines = [];
  let currentLine = [];
  let currentField = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentField += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      currentLine.push(currentField.trim());
      currentField = "";
    } else if ((char === "\r" || char === "\n") && !inQuotes) {
      if (char === "\r" && nextChar === "\n") {
        i++;
      }
      currentLine.push(currentField.trim());
      if (currentLine.some((f) => f !== "")) {
        lines.push(currentLine);
      }
      currentLine = [];
      currentField = "";
    } else {
      currentField += char;
    }
  }

  if (currentField || currentLine.length > 0) {
    currentLine.push(currentField.trim());
    if (currentLine.some((f) => f !== "")) {
      lines.push(currentLine);
    }
  }

  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = lines[0];
  const rows = lines.slice(1);
  return { headers, rows };
};

const CsvPreviewViewer = ({ csvData, filename }) => {
  const [search, setSearch] = useState("");
  const { headers = [], rows = [] } = csvData || {};

  const filteredRows = React.useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((row) =>
      row.some((cell) => String(cell).toLowerCase().includes(q))
    );
  }, [rows, search]);

  return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700 shadow-sm overflow-hidden min-h-[350px]">
      {/* CSV Controls Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3.5 py-2.5 bg-gray-50 dark:bg-dark-750 border-b border-gray-200 dark:border-dark-700 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <TableCellsIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span className="text-xs font-semibold text-gray-800 dark:text-dark-100 truncate max-w-[200px]" title={filename}>
            {filename}
          </span>
          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 shrink-0">
            {rows.length} {rows.length === 1 ? "row" : "rows"} • {headers.length} columns
          </span>
        </div>

        <div className="relative w-full sm:w-56 shrink-0">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search CSV data..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-7 py-1 text-xs rounded-md border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-800 dark:text-dark-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* CSV Data Table */}
      <div className="flex-1 overflow-auto max-h-[55vh]">
        {headers.length === 0 && filteredRows.length === 0 ? (
          <div className="flex items-center justify-center p-8 text-xs text-gray-500 dark:text-dark-400">
            No data found in CSV file.
          </div>
        ) : (
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-gray-100 dark:bg-dark-700 sticky top-0 z-10 border-b border-gray-200 dark:border-dark-600">
              <tr>
                <th className="px-2.5 py-1.5 font-semibold text-gray-400 dark:text-dark-400 w-10 text-center border-r border-gray-200 dark:border-dark-600 select-none">
                  #
                </th>
                {headers.map((h, i) => (
                  <th
                    key={i}
                    className="px-3 py-1.5 font-semibold text-gray-700 dark:text-dark-200 border-r border-gray-200 dark:border-dark-600 last:border-0 whitespace-nowrap"
                  >
                    {h || `Col ${i + 1}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700/50">
              {filteredRows.map((row, rIdx) => (
                <tr
                  key={rIdx}
                  className="hover:bg-primary-50/40 dark:hover:bg-dark-700/50 transition-colors"
                >
                  <td className="px-2.5 py-1.5 text-[11px] text-gray-400 dark:text-dark-400 text-center font-mono border-r border-gray-200/60 dark:border-dark-700/60 bg-gray-50/50 dark:bg-dark-750/50 select-none">
                    {rIdx + 1}
                  </td>
                  {headers.map((_, cIdx) => (
                    <td
                      key={cIdx}
                      className="px-3 py-1.5 text-gray-800 dark:text-dark-100 border-r border-gray-100 dark:border-dark-700/40 last:border-0 whitespace-nowrap"
                    >
                      {row[cIdx] !== undefined ? row[cIdx] : ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export function ViewDocumentModal({
  isOpen,
  onClose,
  document,
  entityType = null,
  onDownload,
  onOpenInNewTab,
  onVerificationAction,
  isStudent = false,
  hideUploadedBy = false,
}) {
  const [imageError, setImageError] = useState(false);
  const [docUrl, setDocUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [csvText, setCsvText] = useState("");
  const { user } = useAuthContext();

  // Reset image error and set loading when document changes
  useEffect(() => {
    if (!isOpen) return;
    setImageError(false);
    setIsLoading(true);
    setCsvData(null);
    setCsvText("");
  }, [isOpen, document]);

  useEffect(() => {
    if (!isOpen || !document) return;
    let cancelled = false;
    let localObjectUrl = null;

    const resolveAndEvaluate = async () => {
      let url = "";

      if (document instanceof File || document instanceof Blob) {
        // Not-yet-uploaded file being previewed locally before upload.
        url = URL.createObjectURL(document);
        localObjectUrl = url;
      } else if (document.previewUrl) {
        url = document.previewUrl;
      } else {
        const filePathKey = document.FilePath || document.filePath || document.FileKey || document.fileKey || document.url || document.Url;
        const documentId = document.id || document.DocumentId;

        // entityType (which tab/context this modal was opened from) is the reliable signal - a
        // job's "Sales" folder document is its own JobId-only row that just reuses the S3 key
        // from whichever lead it was copied from, so a lead-documents/ path prefix does NOT mean
        // the row still has a LeadId. Only fall back to path-sniffing for callers (WHS, templates,
        // etc.) that don't pass entityType at all.
        const lowerPath = typeof filePathKey === 'string' ? filePathKey.toLowerCase() : '';
        const isJobDoc = entityType != null
          ? entityType === DOCUMENT_ENTITY_TYPES.JOB
          : (document.isJobDocument || lowerPath.startsWith('job-documents/'));
        const isLeadOrJobDoc =
          entityType != null ||
          document.isLeadDocument ||
          isJobDoc ||
          lowerPath.startsWith('lead-documents/');
        const fetchPreviewUrl = isJobDoc ? getJobDocumentPreviewUrl : getLeadDocumentPreviewUrl;

        if (filePathKey && typeof filePathKey === 'string' && (filePathKey.startsWith('http://') || filePathKey.startsWith('https://'))) {
          url = filePathKey;
        } else if (isLeadOrJobDoc && documentId && !document.isAdditionalDocument) {
          const result = await fetchPreviewUrl(documentId);
          url = result.success ? (result.data?.url || "") : "";
        } else if (filePathKey) {
          const compId = document?.companyId || document?.CompanyId || null;
          const feat = document?.feature || document?.Feature || (typeof filePathKey === 'string' && filePathKey.toLowerCase().startsWith('selection-option-docs/') ? 'SelectionOptionAttachment' : null);
          const result = await getAttachmentPreviewUrl(filePathKey, compId, feat);
          url = result.success ? (result.url || "") : "";
        } else if (documentId) {
          const result = await fetchPreviewUrl(documentId);
          url = result.success ? (result.data?.url || "") : "";
        }
      }

      if (cancelled) return;
      setDocUrl(url);

      // Compute preview support to prevent infinite loading on unsupported files
      const fileNameForCategory = document?.name || document?.FileName || document?.fileName || document?.originalFilename || document?.OriginalFilename || "";
      const { category: cat } = getFileTypeCategory(fileNameForCategory);
      const isImg = cat === "IMAGE";
      const isOffice = cat === "DOCX" || cat === "EXCEL" || cat === "POWERPOINT";
      const isCsvFile = fileNameForCategory.toLowerCase().endsWith(".csv");

      // Check if localhost (office files cannot preview on localhost)
      let isLocalHostUrl = false;
      if (url) {
        try {
          const parsed = new URL(url);
          const hostname = parsed.hostname.toLowerCase();
          isLocalHostUrl =
            hostname === "localhost" ||
            hostname === "127.0.0.1" ||
            hostname === "[::1]" ||
            hostname.startsWith("192.168.") ||
            hostname.startsWith("10.") ||
            /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname);
        } catch (e) {
          // ignore
        }
      }

      const isOfficePreviewable = isOffice && !isLocalHostUrl;

      if (!url || (!isImg && !isCsvFile && !isOfficePreviewable)) {
        setIsLoading(false);
      }
    };

    resolveAndEvaluate();

    return () => {
      cancelled = true;
      if (localObjectUrl) {
        URL.revokeObjectURL(localObjectUrl);
      }
    };
  }, [isOpen, document, entityType]);

  // Load CSV content when document or docUrl is available
  useEffect(() => {
    if (!isOpen || !document) return;
    const fileNameForCsv = document?.name || document?.FileName || document?.fileName || document?.originalFilename || document?.OriginalFilename || "";
    const isCsvFile = fileNameForCsv.toLowerCase().endsWith(".csv");

    if (!isCsvFile) return;

    setIsLoading(true);

    if (document instanceof File || document instanceof Blob) {
      document
        .text()
        .then((text) => {
          setCsvText(text);
          const parsed = parseCsvText(text);
          setCsvData(parsed);
        })
        .catch((err) => {
          console.error("Error reading CSV blob:", err);
          setCsvData(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
      return;
    }

    if (docUrl) {
      fetch(docUrl)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch CSV stream");
          return res.text();
        })
        .then((text) => {
          setCsvText(text);
          const parsed = parseCsvText(text);
          setCsvData(parsed);
        })
        .catch((err) => {
          console.error("Error loading CSV file:", err);
          setCsvData(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, docUrl, document]);

  if (!isOpen || !document) return null;

  const fileName = document?.name || document?.FileName || document?.fileName || document?.originalFilename || document?.OriginalFilename || "";
  const { category, config } = getFileTypeCategory(fileName);
  const Icon = config.icon;
  const isImage = category === "IMAGE";
  const isCsv = fileName.toLowerCase().endsWith(".csv");
  const fileSize = document.size || document.FileSize || document.FileSizeBytes || document.fileSizeBytes || document.fileSize || 0;

  const isLocalPreview = document instanceof File || document instanceof Blob;

  const rawUpload = document?.UploadedOn || document?.uploadedOn || document?.UploadedDate || document?.uploadedDate || document?.UploadedAt || document?.uploadedAt || document?.CreatedOn || document?.createdOn || document?.CreatedDate || document?.createdDate || document?.CreatedAt || document?.createdAt;
  const uploadDate = rawUpload ? formatDate(rawUpload) : (isLocalPreview ? formatDisplayDate(new Date()) : "-");

  const rawModified = document?.ModifiedOn || document?.modifiedOn || document?.ModifiedDate || document?.modifiedDate || document?.ModifiedAt || document?.modifiedAt || document?.UpdatedAt || document?.updatedAt;
  const modifiedDate = rawModified ? formatDate(rawModified) : "-";

  let uploadedBy = document?.UploadedByName || document?.uploadedByName || document?.UploadedBy || document?.uploadedBy;
  if (!uploadedBy || uploadedBy === "System" || isLocalPreview) {
    const candidate = getUploaderName(document);
    uploadedBy = (candidate && candidate !== "System") ? candidate : (user?.FullName || user?.fullName || user?.name || user?.unique_name || candidate || "System");
  }

  const modifiedBy = document?.ModifiedByName || getModifierName(document);
  const filePath = document.filePath || document.path || document.FilePath || "";
  const verificationInfo = getVerificationMetadataLabels(
    document.verificationStatus,
  );
  const hasVerificationMeta =
    document.verifiedBy || document.verifiedAt || document.verificationRemark;

  // Check if this document is from history
  const isHistoryDocument = document.isHistoryItem || document.History;

  const isLocalHost = (() => {
    if (!docUrl) return false;
    try {
      const parsed = new URL(docUrl);
      const hostname = parsed.hostname.toLowerCase();
      return (
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname === "[::1]" ||
        hostname.startsWith("192.168.") ||
        hostname.startsWith("10.") ||
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)
      );
    } catch (e) {
      return false;
    }
  })();

  const handleDownload = async () => {
    if (onDownload) {
      await onDownload(document);
    } else if (docUrl && (docUrl.startsWith("http") || docUrl.startsWith("blob:"))) {
      const toastId = toast.loading(`Preparing download...`);
      try {
        const response = await fetch(docUrl);
        if (!response.ok) throw new Error("Network response was not ok");
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = window.document.createElement("a");
        link.href = blobUrl;
        link.download = fileName || "document";
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 200);
        toast.success("Download started", { id: toastId });
      } catch (error) {
        console.warn("Direct fetch failed for download, executing anchor fallback:", error);
        toast.dismiss(toastId);
        const link = window.document.createElement("a");
        link.href = docUrl;
        link.download = fileName || "document";
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
      }
    } else {
      await downloadDocumentWithToast(document);
    }
  };

  const handleOpenInNewTab = () => {
    const fileName = document?.name || document?.FileName || "";
    const isCsvFile = fileName.toLowerCase().endsWith(".csv");

    if (isCsvFile && csvText) {
      const blob = new Blob([csvText], { type: "text/plain;charset=utf-8" });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      return;
    }

    if (docUrl) {
      window.open(docUrl, "_blank");
      return;
    }
    if (onOpenInNewTab) {
      onOpenInNewTab(document);
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  // Safe formatting function for file size
  const safeFormatBytes = (bytes) => {
    try {
      if (typeof bytes !== "number" || isNaN(bytes) || bytes < 0) {
        return "Unknown size";
      }
      return formatBytes(bytes);
    } catch (error) {
      console.warn("Error formatting bytes:", error);
      return "Unknown size";
    }
  };
  const modalContent = (
    <Transition show={isOpen}>
      <Dialog open={true} onClose={() => { }} static autoFocus className="relative z-[99999]">
        <TransitionChild
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="fixed inset-0 z-[99998] bg-gray-900/75 backdrop-blur-sm transition-opacity"
        />

        <TransitionChild
          as={DialogPanel}
          enter="ease-out transform-gpu transition-all duration-300"
          enterFrom="opacity-0 scale-95 translate-y-4"
          enterTo="opacity-100 scale-100 translate-y-0"
          leave="ease-in transform-gpu transition-all duration-200"
          leaveFrom="opacity-100 scale-100 translate-y-0"
          leaveTo="opacity-0 scale-95 translate-y-4"
          className="fixed inset-0 z-[99999] flex items-center justify-center p-2 sm:p-4"
        >
          {/* Responsive Modal Container */}
          <div className="flex h-[92vh] sm:h-[90vh] w-full max-w-5xl transform-gpu flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900 border border-slate-200/80">
            {/* Responsive Header - Fixed Height */}
            <div className="flex h-14 sm:h-16 flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-3.5 sm:px-6 dark:border-gray-700 dark:bg-gray-900">
              <div className="flex items-center gap-2.5 min-w-0 flex-1 pr-2">
                <div className={`flex h-9 w-9 sm:h-11 sm:w-11 flex-shrink-0 items-center justify-center rounded-xl ${config.bgColor} ${config.darkBgColor}`}>
                  <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${config.color}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-xs sm:text-base font-bold text-gray-900 dark:text-gray-100 truncate" title={fileName}>
                    {fileName || "Document Preview"}
                  </h2>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 truncate">
                      {fileSize > 0 ? `${safeFormatBytes(fileSize)} • ` : ""}{config.label}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleOpenInNewTab}
                  className="p-1.5 sm:px-3 sm:py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 dark:border-gray-700 dark:hover:bg-gray-800 text-slate-600 dark:text-gray-300 hover:text-slate-900 text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer"
                  title="Open in new tab"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Open in new tab</span>
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-800 text-slate-500 hover:text-slate-800 transition-all cursor-pointer"
                  title="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Responsive Content Area - Flex 1 with proper overflow */}
            <div className="flex-1 overflow-hidden">
              <div className="h-full">
                {/* Mobile Layout - Full height preview */}
                <div className="flex h-full flex-col lg:hidden">
                  {/* Compact Mobile Metadata Bar */}
                  <div className="flex-shrink-0 bg-slate-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-3.5 py-1.5 flex items-center justify-between gap-2 text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                    <div className="flex items-center gap-3 truncate">
                      <span><strong>Date:</strong> {uploadDate}</span>
                      {!hideUploadedBy && uploadedBy && <span className="truncate"><strong>By:</strong> {uploadedBy}</span>}
                    </div>
                  </div>

                  {/* Mobile Preview Area */}
                  <div className="flex-1 overflow-hidden bg-slate-900/5 dark:bg-gray-900 p-2 sm:p-4">
                    <div className="h-full w-full flex items-center justify-center relative rounded-xl overflow-hidden">
                      {isLoading && (
                        <div className="flex flex-col items-center justify-center py-12 absolute inset-0 bg-white/80 dark:bg-gray-900/80 z-10 rounded-xl">
                          <div className="h-8 w-8 animate-spin rounded-full border-3 border-slate-300 border-t-primary-600" />
                          <span className="text-xs font-semibold text-slate-500 mt-2">Loading preview...</span>
                        </div>
                      )}
                      {isCsv && csvData ? (
                        <div className={`w-full h-full ${isLoading ? "invisible" : "visible"}`}>
                          <CsvPreviewViewer csvData={csvData} filename={fileName} />
                        </div>
                      ) : isImage && docUrl && !imageError ? (
                        <img
                          src={docUrl}
                          alt={fileName}
                          className={`max-w-full max-h-full object-contain rounded-xl shadow-md ${isLoading ? "invisible" : "visible"}`}
                          onError={() => {
                            setIsLoading(false);
                            handleImageError();
                          }}
                          onLoad={() => setIsLoading(false)}
                        />
                      ) : (category === "PDF" || (category === "TEXT" && !isCsv)) && docUrl ? (
                        <iframe
                          src={docUrl}
                          title={fileName}
                          className={`w-full h-full rounded-xl border border-gray-200 bg-white ${isLoading ? "invisible" : "visible"}`}
                          frameBorder="0"
                          onLoad={() => setIsLoading(false)}
                        />
                      ) : (category === "DOCX" || category === "EXCEL" || category === "POWERPOINT") && docUrl && !isLocalHost ? (
                        <iframe
                          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                            docUrl,
                          )}`}
                          title={fileName}
                          className={`w-full h-full rounded-xl border border-gray-200 ${isLoading ? "invisible" : "visible"}`}
                          frameBorder="0"
                          onLoad={() => setIsLoading(false)}
                        />
                      ) : !isLoading ? (
                        <div className="text-center py-8 px-4">
                          <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl ${config.bgColor} ${config.darkBgColor} mb-3`}>
                            <Icon className={`h-7 w-7 ${config.color}`} />
                          </div>
                          <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">
                            Preview Not Available
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 max-w-xs mx-auto">
                            {isLocalHost && (category === "DOCX" || category === "EXCEL" || category === "POWERPOINT")
                              ? "Office document previews are disabled on local servers (localhost) because the Microsoft Web Viewer requires a public URL. Please download the document to view it."
                              : "Download the document to view its contents."}
                          </p>
                          <Button
                            variant="solid"
                            color="primary"
                            onClick={handleDownload}
                            size="sm"
                          >
                            <ArrowDownTrayIcon className="mr-1.5 h-4 w-4" />
                            Download
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Desktop Layout - Side by Side */}
                <div className="hidden h-full lg:flex">
                  {/* Sidebar */}
                  <div className="w-72 flex-shrink-0 border-r border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                    <div className="h-full flex flex-col">
                      <div className="flex-1 overflow-y-auto p-4">
                        <div className="space-y-4">
                          {/* Document Name Card */}
                          <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                            <div className="flex items-start gap-3">
                              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${config.bgColor} ${config.darkBgColor} flex-shrink-0`}>
                                <Icon className={`h-5 w-5 ${config.color}`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-all">
                                  {fileName || 'Untitled Document'}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {config.label}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* File Information */}
                          <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-xs">File Information</h4>
                            <div className="space-y-3">
                              {fileSize > 0 && (
                                <InfoItem
                                  icon={FolderIcon}
                                  label="File Size"
                                  value={safeFormatBytes(fileSize)}
                                />
                              )}
                              <InfoItem
                                icon={ClockIcon}
                                label="Upload Date"
                                value={uploadDate}
                              />
                              {!hideUploadedBy && (
                                <InfoItem
                                  icon={UserIcon}
                                  label="Uploaded By"
                                  value={uploadedBy}
                                />
                              )}
                              {modifiedDate !== "-" && (
                                <InfoItem
                                  icon={ClockIcon}
                                  label="Modified Date"
                                  value={modifiedDate}
                                />
                              )}
                              {modifiedBy !== "-" && (
                                <InfoItem
                                  icon={UserIcon}
                                  label="Modified By"
                                  value={modifiedBy}
                                />
                              )}
                            </div>
                          </div>

                          {/* Document History - Show for history documents */}
                          {Boolean(isHistoryDocument) && Array.isArray(document.History) && document.History.length > 0 && (
                            <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-xs flex items-center gap-2">
                                <ClockIcon className="h-3.5 w-3.5 text-gray-400" />
                                Document History
                              </h4>
                              <div className="space-y-3">
                                {document.History.map((historyItem, index) => (
                                  <div key={`${historyItem.Version}-${index}`} className="relative">
                                    {/* Timeline Line */}
                                    {index < document.History.length - 1 && (
                                      <div className="absolute left-4 top-8 bottom-0 w-px bg-gray-200 dark:bg-gray-700" />
                                    )}

                                    <div className="flex items-start space-x-3">
                                      {/* Timeline Dot */}
                                      <div className={`flex-shrink-0 w-8 h-8 rounded-full ${getActionTypeBgColor(historyItem.ActionType)} flex items-center justify-center`}>
                                        <div className={`w-3 h-3 rounded-full ${getActionTypeDotColor(historyItem.ActionType)}`} />
                                      </div>

                                      {/* History Content */}
                                      <div className="min-w-0 flex-1 pb-4">
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                          <Badge
                                            variant="soft"
                                            color={getActionTypeColor(historyItem.ActionType)}
                                            className="text-[11px]"
                                          >
                                            {historyItem.ActionTypeText}
                                          </Badge>
                                          <span className="text-xs text-gray-500 dark:text-gray-400">
                                            Version {historyItem.Version}
                                          </span>
                                        </div>

                                        <div className="flex items-center space-x-2 mt-2">
                                          <Avatar
                                            size={8}
                                            color={colorFromText(historyItem.ActionByName || "Unknown")}
                                          >
                                            {getInitials(historyItem.ActionByName || "Unknown")}
                                          </Avatar>
                                          <div className="min-w-0 flex-1">
                                            <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                                              {historyItem.ActionByName || "Unknown"}
                                            </p>
                                            <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                              {formatDate(historyItem.ActionAt)}
                                            </p>
                                          </div>
                                        </div>

                                        {historyItem.Remark && (
                                          <div className="rounded-lg bg-gray-50 p-2 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                                            {historyItem.Remark}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Description */}
                          {Boolean(document.description) && (
                            <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 text-xs">Description</h4>
                              <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                {document.description}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Desktop Preview Area */}
                  <div className="flex-1 bg-white dark:bg-gray-900 min-h-0">
                    <div className="h-full overflow-hidden p-3">
                      <div className="h-full flex items-center justify-center relative">
                        {isLoading && (
                          <div className="flex flex-col items-center justify-center py-12 absolute inset-0 bg-white/80 dark:bg-gray-900/80 z-10 rounded-xl">
                            <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-primary-500" />
                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-3">Loading preview...</span>
                          </div>
                        )}
                        {isCsv && csvData ? (
                          <div className={`w-full h-full ${isLoading ? "invisible" : "visible"}`}>
                            <CsvPreviewViewer csvData={csvData} filename={fileName} />
                          </div>
                        ) : isImage && docUrl && !imageError ? (
                          <div className={`relative w-full h-full flex items-center justify-center ${isLoading ? "invisible" : "visible"}`}>
                            <img
                              src={docUrl}
                              alt={fileName}
                              className="max-w-full max-h-[60vh] object-contain rounded-xl shadow-lg"
                              onError={() => {
                                setIsLoading(false);
                                handleImageError();
                              }}
                              onLoad={() => setIsLoading(false)}
                            />
                            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg dark:bg-gray-800/90">
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {config.label}
                              </span>
                            </div>
                          </div>
                        ) : (category === "PDF" || (category === "TEXT" && !isCsv)) && docUrl ? (
                          <div className={`w-full h-full ${isLoading ? "invisible" : "visible"}`}>
                            <iframe
                              src={docUrl}
                              title={fileName}
                              className="w-full h-[60vh] rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 bg-white"
                              frameBorder="0"
                              onLoad={() => setIsLoading(false)}
                            />
                          </div>
                        ) : (category === "DOCX" || category === "EXCEL" || category === "POWERPOINT") && docUrl && !isLocalHost ? (
                          <div className={`w-full h-full ${isLoading ? "invisible" : "visible"}`}>
                            <iframe
                              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                                docUrl,
                              )}`}
                              title={fileName}
                              className="w-full h-[60vh] rounded-xl shadow-lg border border-gray-200 dark:border-gray-700"
                              frameBorder="0"
                              onLoad={() => setIsLoading(false)}
                            />
                          </div>
                        ) : !isLoading ? (
                          <div className="text-center py-12">
                            <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-2xl ${config.bgColor} ${config.darkBgColor} shadow-lg mb-4`}>
                              <Icon className={`h-10 w-10 ${config.color}`} />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                              Preview Not Available
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                              {isLocalHost && (category === "DOCX" || category === "EXCEL" || category === "POWERPOINT")
                                ? "Office document previews are disabled on local servers (localhost) because the Microsoft Web Viewer requires a public URL. Please download the document to view it."
                                : "This file type cannot be previewed in the browser. Please download the document to view its contents."}
                            </p>
                            <Button
                              variant="solid"
                              color="primary"
                              onClick={handleDownload}
                              className="shadow-lg"
                            >
                              <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
                              Download Document
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Responsive Footer - Fixed Height */}
            <div className="flex h-14 sm:h-16 flex-shrink-0 items-center justify-between border-t border-gray-200 bg-gray-50 px-3.5 sm:px-6 dark:border-gray-700 dark:bg-gray-800">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 truncate max-w-[50%]">
                {safeFormatBytes(fileSize)} • {config.label}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outlined"
                  color="neutral"
                  onClick={onClose}
                  size="sm"
                  className="px-4 text-xs font-bold cursor-pointer"
                >
                  Close
                </Button>
                <Button
                  variant="solid"
                  color="primary"
                  onClick={handleDownload}
                  size="sm"
                  className="px-4 text-xs font-bold cursor-pointer shadow-sm"
                >
                  <ArrowDownTrayIcon className="mr-1.5 h-3.5 w-3.5" />
                  Download
                </Button>
              </div>
            </div>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  );

  return typeof document !== "undefined" && document.body
    ? createPortal(modalContent, document.body)
    : modalContent;
}

// Helper component for info items
function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-50 dark:bg-gray-800 flex-shrink-0">
        <Icon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400">{label}</p>
        <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 ">{value}</div>
      </div>
    </div>
  );
}

export default ViewDocumentModal